/**
 * /form/<token> — standalone form fill page (2026-09-07, flag `forms`).
 *
 * The buyer-facing surface for a form shared by email outside a
 * Decision Room: pre-discovery qualification, a post-demo survey, a
 * win/loss survey after the deal closes. Same no-login credential
 * model as /space and /d: the token IS the identity (backend
 * routes/formsPublic.js resolves it to a distribution row), so this
 * page never touches auth state.
 *
 * Light-locked like every prospect surface (data-theme="light" +
 * colorScheme), branded from the seller's brand settings.
 *
 * The honeypot input `website_url` is deliberately rendered (hidden
 * from people, visible to bots): the backend drops any submission that
 * fills it with a silent 200, so a scraper gets nothing to learn from.
 */

import { useEffect, useState } from 'react';
import FormFill, { validateRequired, answersForSubmit } from './FormFill';
import { apiRequest, formatDate } from '../utils/apiRequest';

export default function FormPublicPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);
  const [editingAgain, setEditingAgain] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await apiRequest(`/api/forms/public/${encodeURIComponent(token)}`);
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.status === 404 ? 'This form link is no longer valid.' : err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const accent = (data && data.branding && data.branding.primaryColor) || '#7c3aed';

  const submit = async (e) => {
    e.preventDefault();
    if (!data) return;
    const missing = validateRequired(data.form.fields, values);
    if (missing) { setSubmitError(missing); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiRequest(`/api/forms/public/${encodeURIComponent(token)}/responses`, {
        method: 'POST',
        body: { answers: answersForSubmit(data.form.fields, values), website_url: honeypot },
      });
      setDone(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <div data-theme="light" style={{
      minHeight: '100vh', colorScheme: 'light', color: '#0f172a',
      background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #f5f3ff 100%)',
      padding: '32px 16px 64px', boxSizing: 'border-box',
      fontFamily: 'inherit',
    }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>{children}</div>
    </div>
  );

  if (error) {
    return shell(
      <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 16, padding: 24, color: '#991b1b' }}>
        {error}
      </div>
    );
  }
  if (!data) {
    return shell(<div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Loading…</div>);
  }

  const { form, viewer, branding } = data;
  const alreadySubmitted = viewer && viewer.alreadySubmittedAt;
  const showForm = !done && (!alreadySubmitted || editingAgain);

  return shell(
    <>
      <header style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        {branding && branding.logoDataUrl ? (
          <img src={branding.logoDataUrl} alt="" style={{ height: 36, maxWidth: 160, objectFit: 'contain' }} />
        ) : (
          <div aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 10, background: accent }} />
        )}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8' }}>
            {branding && branding.companyName ? branding.companyName : 'A few questions'}
          </div>
          {viewer && viewer.stakeholderName && (
            <div style={{ fontSize: 14, color: '#475569' }}>For {viewer.stakeholderName}</div>
          )}
        </div>
      </header>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: '28px 28px 24px', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 26, lineHeight: 1.2, fontWeight: 700, color: '#0f172a' }}>{form.name}</h1>
        {form.description && (
          <p style={{ margin: '0 0 22px', fontSize: 15, lineHeight: 1.6, color: '#475569' }}>{form.description}</p>
        )}

        {done ? (
          <div role="status" style={{ padding: '18px 16px', borderRadius: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', fontSize: 15 }}>
            <strong>Thank you.</strong> Your answers were sent{branding && branding.companyName ? ` to ${branding.companyName}` : ''}.
          </div>
        ) : alreadySubmitted && !editingAgain ? (
          <div style={{ padding: '16px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#334155', fontSize: 15 }}>
            You already answered this on {formatDate(viewer.alreadySubmittedAt)}.
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={() => setEditingAgain(true)} style={btn(accent, true)}>Update my answers</button>
            </div>
            <div style={{ marginTop: 8, fontSize: 13, color: '#64748b' }}>Submitting again replaces your earlier answers.</div>
          </div>
        ) : null}

        {showForm && (
          <form onSubmit={submit} noValidate>
            <FormFill
              fields={form.fields}
              values={values}
              onChange={(id, v) => setValues((prev) => ({ ...prev, [id]: v }))}
              accent={accent}
              tone="light"
              disabled={submitting}
            />
            {/* Honeypot: off-screen and skipped by keyboards. Bots fill it; people never see it. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
              <label>Website<input type="text" name="website_url" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} /></label>
            </div>
            {submitError && (
              <div role="alert" style={{ marginTop: 16, padding: '10px 12px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 14 }}>
                {submitError}
              </div>
            )}
            <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button type="submit" disabled={submitting} style={btn(accent)}>
                {submitting ? 'Sending…' : 'Send answers'}
              </button>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>Powered by TrailerCast</span>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

function btn(accent, ghost = false) {
  return {
    padding: '11px 18px', borderRadius: 10, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${accent}`, background: ghost ? '#fff' : accent, color: ghost ? accent : '#fff',
  };
}
